/**
 * SettingsDialog: Floating, draggable, resizable settings window.
 * No overlay blur — sits on top of the 3D scene so you can tweak while viewing.
 * Each section has its own sliders and a "Reset Defaults" button.
 * Uses the same container/slider/font styles as the main editor UI.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useEditorStore } from '../../store/editorStore';
import type { MannequinSettings, GameplaySettings, InterfaceSettings, ParticleSettings, ObstacleConfigKey, ObstacleShape } from '../../store/editorStore';
import { OBSTACLE_CONFIG_KEYS } from '../../store/editorStore';
import SliderRow from '../ui/SliderRow';
import ThemeControls from '../ui/ThemeControls';
import { useTheme } from '../ui/themeStore';
import { IcFigure, IcGamepad, IcCamera, IcPalette, IcSparkle, IcParticles, IcKeyboard, IcMonitor, IcBolt } from '../ui/icons';
import PerformanceSection from './PerformanceSection';
import { color, font, gradient, hexToRgba, segmentBtn, ui, z } from '../ui/theme';
import {
  windowStyle,
  titleBarStyle,
  titleStyle,
  closeBtnStyle,
  bodyStyle,
  tabListStyle,
  tabBtnStyle,
  contentStyle,
  sectionStyle,
  sectionLabel,
  subSectionLabel,
  resetBtnStyle,
  hintTextStyle,
  accordionBtnStyle,
  applyBtnStyle,
  resizeHandleStyle,
  colorRowStyle,
  colorLabelStyle,
  colorInputStyle,
  colorHexStyle,
  toggleRowStyle,
  toggleTrackStyle,
  toggleThumbStyle,
  fieldLabelStyle,
  selectStyle,
  checkboxLabelStyle,
  checkboxStyle,
  kbHeaderRow,
  kbRow,
  kbKeyBtn,
} from './SettingsDialog.styles';

// ── Tab Definitions (add new sections here) ──────────────────────────────────

interface SettingsTab {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const SETTINGS_TABS: SettingsTab[] = [
  { id: 'ui', label: 'UI', icon: <IcMonitor size={16} /> },
  { id: 'performance', label: 'Perf', icon: <IcBolt size={16} /> },
  { id: 'mannequin', label: 'Mannequin', icon: <IcFigure size={16} /> },
  { id: 'gameplay',  label: 'Gameplay',  icon: <IcGamepad size={16} /> },
  { id: 'camera',    label: 'Camera',    icon: <IcCamera size={16} /> },
  { id: 'interface', label: 'Interface', icon: <IcPalette size={16} /> },
  { id: 'particles', label: 'Particles', icon: <IcParticles size={16} /> },
  { id: 'keybindings', label: 'Keys', icon: <IcKeyboard size={16} /> },
];

const MIN_WIDTH = 440;
const MIN_HEIGHT = 360;
const DEFAULT_WIDTH = 780;
const DEFAULT_HEIGHT = 720;

// ── Drag / Resize Hook ──────────────────────────────────────────────────────

function useDragResize() {
  const [pos, setPos] = useState<{ x: number; y: number }>(() => {
    // Center of screen by default
    const x = Math.max(20, Math.round((window.innerWidth - DEFAULT_WIDTH) / 2));
    const y = Math.max(20, Math.round((window.innerHeight - DEFAULT_HEIGHT) / 2));
    return { x, y };
  });
  const [size, setSize] = useState({ w: DEFAULT_WIDTH, h: DEFAULT_HEIGHT });
  const dragging = useRef(false);
  const resizing = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({ mx: 0, my: 0, w: 0, h: 0 });

  // ── Drag handlers ──
  const onDragStart = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragging.current = true;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
  }, [pos]);

  // ── Resize handlers ──
  const onResizeStart = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    resizing.current = true;
    resizeStart.current = { mx: e.clientX, my: e.clientY, w: size.w, h: size.h };
  }, [size]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (dragging.current) {
        const newX = Math.max(0, Math.min(window.innerWidth - 80, e.clientX - dragOffset.current.x));
        const newY = Math.max(0, Math.min(window.innerHeight - 40, e.clientY - dragOffset.current.y));
        setPos({ x: newX, y: newY });
      }
      if (resizing.current) {
        const dx = e.clientX - resizeStart.current.mx;
        const dy = e.clientY - resizeStart.current.my;
        setSize({
          w: Math.max(MIN_WIDTH, resizeStart.current.w + dx),
          h: Math.max(MIN_HEIGHT, resizeStart.current.h + dy),
        });
      }
    };
    const onUp = () => {
      dragging.current = false;
      resizing.current = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  return { pos, size, onDragStart, onResizeStart };
}

// ── Section Container (uses UI container settings from store) ────────────────

function SectionBox({ title, children, style }: { title?: string; children: React.ReactNode; style?: React.CSSProperties }) {
  const iface = useEditorStore(s => s.interfaceSettings);
  const boxStyle: React.CSSProperties = {
    background: hexToRgba(iface.uiContainerBg, iface.uiContainerBgOpacity),
    // Outline + title follow the theme accent (so they whiten with the White
    // preset) instead of a fixed purple hex from interfaceSettings.
    border: `${iface.uiContainerOutlineWidth}px solid var(--line-accent)`,
    borderRadius: 10,
    padding: iface.uiContainerPadding + 2,
    marginBottom: iface.uiContainerMargin + 2,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: iface.uiContainerGap,
    ...style,
  };
  const titleStyle: React.CSSProperties = {
    fontSize: iface.typoTitleSize,
    color: 'var(--accent-soft)',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    marginBottom: iface.typoTitleMarginBottom,
    fontFamily: font.sans,
  };
  return (
    <div style={boxStyle}>
      {title && <div style={titleStyle}>{title}</div>}
      {children}
    </div>
  );
}

// ── Dynamic sub-section label (reads typography settings) ─────────────────────

function SubLabel({ children }: { children: React.ReactNode }) {
  const iface = useEditorStore(s => s.interfaceSettings);
  return (
    <div style={{
      fontSize: iface.typoSubtitleSize,
      color: 'var(--accent-soft)',
      fontWeight: 600,
      textTransform: 'uppercase' as const,
      letterSpacing: 1,
      marginTop: iface.typoSubtitleMarginTop,
      marginBottom: iface.typoSubtitleMarginBottom,
      fontFamily: font.sans,
    }}>
      {children}
    </div>
  );
}

// ── Dynamic hint text (reads typography settings) ─────────────────────────────

function HintText({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  const iface = useEditorStore(s => s.interfaceSettings);
  return (
    <div style={{
      ...hintTextStyle,
      fontSize: iface.typoValueSize,
      color: 'oklch(0.62 calc(0.05 * var(--accent-c)) var(--accent-h))',
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── Dynamic field label (reads typography settings) ───────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  const iface = useEditorStore(s => s.interfaceSettings);
  return (
    <span style={{
      ...fieldLabelStyle,
      fontSize: iface.typoBodySize,
      color: 'oklch(0.72 calc(0.05 * var(--accent-c)) var(--accent-h))',
    }}>
      {children}
    </span>
  );
}

// ── Dynamic section wrapper (reads container gap) ─────────────────────────────

function SectionWrap({ children }: { children: React.ReactNode }) {
  const gap = useEditorStore(s => s.interfaceSettings.uiContainerGap);
  return (
    <div style={{ ...sectionStyle, gap }}>
      {children}
    </div>
  );
}

// ── Main Dialog ──────────────────────────────────────────────────────────────

export default function SettingsDialog() {
  const open = useEditorStore(s => s.settingsOpen);
  const setOpen = useEditorStore(s => s.setSettingsOpen);
  const [activeTab, setActiveTab] = useState('ui');
  const { pos, size, onDragStart, onResizeStart } = useDragResize();

  const tabs = SETTINGS_TABS;

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  if (!open) return null;

  return (
    <div
      style={{
        ...windowStyle,
        // Clamped to the viewport so the window always stays reachable and
        // fits small screens (drag/resize state may exceed a smaller window).
        // The extra width/height terms keep the far edges on-screen even when
        // the window sits at a non-zero left/top.
        left: `min(${pos.x}px, calc(100vw - 90px))`,
        top: `min(${pos.y}px, calc(100vh - 60px))`,
        width: `min(${size.w}px, calc(100vw - 16px), calc(100vw - ${Math.max(0, pos.x)}px - 8px))`,
        height: `min(${size.h}px, calc(100vh - 16px), calc(100vh - ${Math.max(0, pos.y)}px - 8px))`,
      }}
    >
      {/* Title bar — draggable */}
      <div style={titleBarStyle} onMouseDown={onDragStart}>
        <div style={titleStyle}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ stroke: 'oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h))' }} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          <span>Settings</span>
        </div>
        <button
          style={closeBtnStyle}
          onClick={() => setOpen(false)}
          onMouseDown={e => e.stopPropagation()}
        >
          {'\u2715'}
        </button>
      </div>

      {/* Body: tab list + content */}
      <div style={bodyStyle}>
        {/* Left: Tab list */}
        <div style={tabListStyle}>
          {tabs.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  ...tabBtnStyle,
                  background: active
                    ? 'linear-gradient(135deg, oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.2), oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.1))'
                    : 'transparent',
                  borderColor: active ? 'oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.4)' : 'transparent',
                  color: active ? color.accent : color.ink,
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Right: Content */}
        <div style={contentStyle}>
          {activeTab === 'mannequin' && <MannequinSection />}
          {activeTab === 'gameplay' && <GameplaySection />}
          {activeTab === 'camera' && <CameraSection />}
          {activeTab === 'interface' && <InterfaceSection />}
          {activeTab === 'particles' && <ParticlesSection />}
          {activeTab === 'keybindings' && <KeybindingsSection />}
          {activeTab === 'ui' && <UISection />}
          {activeTab === 'performance' && <PerformanceSection />}
        </div>
      </div>

      {/* Resize handle — bottom right corner */}
      <div style={resizeHandleStyle} onMouseDown={onResizeStart}>
        <svg width="12" height="12" viewBox="0 0 12 12" style={{ opacity: 0.4 }}>
          <path d="M11 1L1 11M11 5L5 11M11 9L9 11" style={{ stroke: 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h))' }} strokeWidth="1.2" />
        </svg>
      </div>
    </div>
  );
}

// ── Mannequin Section ────────────────────────────────────────────────────────

function MannequinSection() {
  const settings = useEditorStore(s => s.mannequinSettings);
  const setSettings = useEditorStore(s => s.setMannequinSettings);
  const reset = useEditorStore(s => s.resetMannequinSettings);

  const set = (field: keyof MannequinSettings) => (v: number) => setSettings({ [field]: v });

  return (
    <SectionWrap>
      <SectionBox title="Body Proportions">
        <SliderRow label="Height" value={settings.figureHeight} min={0.7} max={1.4} step={0.05}
          format={v => `${(v * 100).toFixed(0)}%`} onChange={set('figureHeight')} />
        <SliderRow label="Head Size" value={settings.headRadius} min={0.05} max={0.25} step={0.01}
          format={v => v.toFixed(2)} onChange={set('headRadius')} />
        <SliderRow label="Shoulder W" value={settings.shoulderWidth} min={0.10} max={0.45} step={0.01}
          format={v => v.toFixed(2)} onChange={set('shoulderWidth')} />
        <SliderRow label="Upper Arm" value={settings.upperArmLength} min={0.20} max={0.80} step={0.02}
          format={v => v.toFixed(2)} onChange={set('upperArmLength')} />
        <SliderRow label="Lower Arm" value={settings.lowerArmLength} min={0.20} max={0.80} step={0.02}
          format={v => v.toFixed(2)} onChange={set('lowerArmLength')} />
        <SliderRow label="Hand Size" value={settings.handRadius} min={0.03} max={0.15} step={0.01}
          format={v => v.toFixed(2)} onChange={set('handRadius')} />
        <SliderRow label="Torso W" value={settings.torsoRadius} min={0.02} max={0.06} step={0.005}
          format={v => v.toFixed(3)} onChange={set('torsoRadius')} />
      </SectionBox>

      <SectionBox title="Animation">
        <SliderRow label="Anticipation" value={settings.anticipationBeats} min={0.1} max={2.0} step={0.1}
          format={v => `${v.toFixed(1)}b`} onChange={set('anticipationBeats')} />
        <SliderRow label="Smoothing" value={settings.lerpSpeed} min={2.0} max={20.0} step={0.5}
          format={v => v.toFixed(1)} onChange={set('lerpSpeed')} />
      </SectionBox>

      <button style={resetBtnStyle} onClick={reset}>
        Reset Mannequin Defaults
      </button>
    </SectionWrap>
  );
}

// ── Gameplay Section ─────────────────────────────────────────────────────────

const SHAPE_OPTIONS: { value: ObstacleShape; label: string }[] = [
  { value: 'regular', label: 'Regular' },
  { value: 'diagonal', label: 'Diagonal' },
  { value: 'center', label: 'Center' },
  { value: 'square', label: 'Square' },
  { value: 'triangle', label: 'Triangle' },
];

const OBSTACLE_LABELS: Record<ObstacleConfigKey, string> = {
  wall_left: 'Left Wall',
  wall_right: 'Right Wall',
  angle_left: 'Diagonal Left',
  angle_right: 'Diagonal Right',
  center: 'Center',
  crouch: 'Crouch',
};

function ObstacleConfigPanel({ configKey }: { configKey: ObstacleConfigKey }) {
  const cfg = useEditorStore(s => s.gameplaySettings.obstacleConfigs[configKey]);
  const setObs = useEditorStore(s => s.setObstacleConfig);
  const containerGap = useEditorStore(s => s.interfaceSettings.uiContainerGap);
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ marginBottom: 4 }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={accordionBtnStyle}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {OBSTACLE_LABELS[configKey]}
        </span>
        <span style={{ fontSize: 8, opacity: 0.6 }}>{expanded ? '\u25B2' : '\u25BC'}</span>
      </button>

      {expanded && (
        <div style={{ padding: '8px 0 2px 10px', borderLeft: '2px solid oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.15)', display: 'flex', flexDirection: 'column', gap: containerGap }}>
          {/* Shape */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <FieldLabel>Shape</FieldLabel>
            <select
              value={cfg.shape}
              onChange={e => setObs(configKey, { shape: e.target.value as ObstacleShape })}
              style={selectStyle}
            >
              {SHAPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <SliderRow label="Scale X" value={cfg.scaleX} min={0.1} max={3} step={0.05}
            format={v => v.toFixed(2)} onChange={v => setObs(configKey, { scaleX: v })} />
          <SliderRow label="Scale Y" value={cfg.scaleY} min={0.1} max={3} step={0.05}
            format={v => v.toFixed(2)} onChange={v => setObs(configKey, { scaleY: v })} />
          <SliderRow label="Scale Z" value={cfg.scaleZ} min={0.1} max={3} step={0.05}
            format={v => v.toFixed(2)} onChange={v => setObs(configKey, { scaleZ: v })} />
          <SliderRow label="Pos X" value={cfg.defaultX} min={-3} max={3} step={0.05}
            format={v => v.toFixed(2)} onChange={v => setObs(configKey, { defaultX: v })} />
          <SliderRow label="Pos Y" value={cfg.defaultY} min={-3} max={3} step={0.05}
            format={v => v.toFixed(2)} onChange={v => setObs(configKey, { defaultY: v })} />
          <SliderRow label="Rotation" value={cfg.rotation} min={0} max={360} step={5}
            format={v => `${v}\u00B0`} onChange={v => setObs(configKey, { rotation: v })} />
        </div>
      )}
    </div>
  );
}

function GameplaySection() {
  const settings = useEditorStore(s => s.gameplaySettings);
  const setSettings = useEditorStore(s => s.setGameplaySettings);
  const reset = useEditorStore(s => s.resetGameplaySettings);

  const set = (field: keyof GameplaySettings) => (v: number) => setSettings({ [field]: v });

  return (
    <SectionWrap>
      <SectionBox title="Notes">
        <SliderRow label="Note Size" value={settings.noteSize} min={0.08} max={0.35} step={0.005}
          format={v => v.toFixed(3)} onChange={set('noteSize')} />
        <SliderRow label="Rail Width" value={settings.railWidth} min={2} max={20} step={1}
          format={v => `${v}px`} onChange={set('railWidth')} />
        <SliderRow label="Rail Node Size" value={settings.railNodeSize} min={0.04} max={0.20} step={0.005}
          format={v => v.toFixed(3)} onChange={set('railNodeSize')} />
      </SectionBox>

      <SectionBox title="Note Colors">
        <ColorRow label="Left Hand" value={settings.noteColorLeft}
          onChange={v => setSettings({ noteColorLeft: v })} />
        <ColorRow label="Right Hand" value={settings.noteColorRight}
          onChange={v => setSettings({ noteColorRight: v })} />
        <ColorRow label="One Hand Special" value={settings.noteColorOneHand}
          onChange={v => setSettings({ noteColorOneHand: v })} />
        <ColorRow label="Both Hands Special" value={settings.noteColorBothHands}
          onChange={v => setSettings({ noteColorBothHands: v })} />
      </SectionBox>

      <SectionBox title="Note Material">
        <SliderRow label="Metalness" value={settings.noteMetalness} min={0} max={1} step={0.05}
          format={v => `${(v * 100).toFixed(0)}%`} onChange={set('noteMetalness')} />
        <SliderRow label="Roughness" value={settings.noteRoughness} min={0} max={1} step={0.05}
          format={v => `${(v * 100).toFixed(0)}%`} onChange={set('noteRoughness')} />
        <ToggleRow label="Fresnel Rim Glow" value={settings.noteFresnelEnabled}
          onChange={v => setSettings({ noteFresnelEnabled: v })} />
        {settings.noteFresnelEnabled && (
          <>
            <ColorRow label="Fresnel Color" value={settings.noteFresnelColor}
              onChange={v => setSettings({ noteFresnelColor: v })} />
            <SliderRow label="Fresnel Intensity" value={settings.noteFresnelIntensity} min={0} max={3} step={0.1}
              format={v => v.toFixed(1)} onChange={set('noteFresnelIntensity')} />
            <SliderRow label="Fresnel Power" value={settings.noteFresnelPower} min={0.5} max={5} step={0.1}
              format={v => v.toFixed(1)} onChange={set('noteFresnelPower')} />
          </>
        )}
      </SectionBox>

      <SectionBox title="Obstacles (Global)">
        <SliderRow label="Base Scale" value={settings.obstacleScale} min={0.01} max={0.5} step={0.005}
          format={v => v.toFixed(3)} onChange={set('obstacleScale')} />
        <ColorRow label="Fill Color" value={settings.obstacleFillColor}
          onChange={v => setSettings({ obstacleFillColor: v })} />
        <SliderRow label="Fill Opacity" value={settings.obstacleFillOpacity} min={0} max={1} step={0.05}
          format={v => `${(v * 100).toFixed(0)}%`} onChange={v => setSettings({ obstacleFillOpacity: v })} />
        <ColorRow label="Fresnel Color" value={settings.obstacleFresnelColor}
          onChange={v => setSettings({ obstacleFresnelColor: v })} />
        <SliderRow label="Fresnel Opacity" value={settings.obstacleFresnelOpacity} min={0} max={1} step={0.05}
          format={v => `${(v * 100).toFixed(0)}%`} onChange={v => setSettings({ obstacleFresnelOpacity: v })} />
        <SliderRow label="Fresnel Intensity" value={settings.obstacleFresnelIntensity} min={0} max={5} step={0.1}
          format={v => v.toFixed(1)} onChange={v => setSettings({ obstacleFresnelIntensity: v })} />
        <ColorRow label="Outline Color" value={settings.obstacleOutlineColor}
          onChange={v => setSettings({ obstacleOutlineColor: v })} />
        <SliderRow label="Outline Emission" value={settings.obstacleOutlineEmission} min={0} max={5} step={0.1}
          format={v => v.toFixed(1)} onChange={v => setSettings({ obstacleOutlineEmission: v })} />
      </SectionBox>

      <SectionBox title="Per-Obstacle Settings">
        {OBSTACLE_CONFIG_KEYS.map(key => (
          <ObstacleConfigPanel key={key} configKey={key} />
        ))}
      </SectionBox>

      <SectionBox title="Sound Effects">
        <HintText>The voice each note hit plays, plus the sounds for placing / deleting a note and scrubbing with the wheel.</HintText>
        <SubLabel>Note Hit</SubLabel>
        <SegmentRow
          options={['tone', 'clap'] as const}
          value={settings.noteSound ?? 'tone'}
          onChange={v => setSettings({ noteSound: v })}
        />
        <HintText>“Clap” is the hit sound from the Synths Riders desktop editor.</HintText>

        <SubLabel>Place Note</SubLabel>
        <SegmentRow
          options={['pop', 'blip', 'thock', 'quack', 'none'] as const}
          value={settings.placeSound}
          onChange={v => setSettings({ placeSound: v })}
        />
        <SliderRow label="Volume" value={settings.placeVolume} min={0} max={1} step={0.05}
          format={v => `${(v * 100).toFixed(0)}%`} onChange={set('placeVolume')} />

        <SubLabel>Delete Note</SubLabel>
        <SegmentRow
          options={['shatter', 'zap', 'thud', 'none'] as const}
          value={settings.deleteSound}
          onChange={v => setSettings({ deleteSound: v })}
        />
        <SliderRow label="Volume" value={settings.deleteVolume} min={0} max={1} step={0.05}
          format={v => `${(v * 100).toFixed(0)}%`} onChange={set('deleteVolume')} />

        <SubLabel>Scroll Tick</SubLabel>
        <SegmentRow
          options={['classic', 'click', 'soft'] as const}
          value={settings.scrollSound}
          onChange={v => setSettings({ scrollSound: v })}
        />
        <SliderRow label="Volume" value={settings.scrollVolume} min={0} max={1} step={0.05}
          format={v => `${(v * 100).toFixed(0)}%`} onChange={set('scrollVolume')} />
      </SectionBox>

      <button style={resetBtnStyle} onClick={reset}>
        Reset Gameplay Defaults
      </button>
    </SectionWrap>
  );
}

// ── Camera Section ─────────────────────────────────────────────────────────

const BUILTIN_DEFAULTS: Record<string, { position: [number, number, number]; rotation: [number, number, number] }> = {
  default: { position: [0, 1.6, 3], rotation: [-0.15, 0, 0] },
  left: { position: [-3, 1.6, 1], rotation: [-0.1, -0.8, -0.05] },
  right: { position: [3, 1.6, 1], rotation: [-0.1, 0.8, 0.05] },
  custom: { position: [0, 1.6, 3], rotation: [-0.15, 0, 0] },
};

function CameraPresetEditor({ label, presetKey, pos, rot, setPos, setRot, onReset, onApply }: {
  label: string;
  presetKey: string;
  pos: [number, number, number];
  rot: [number, number, number];
  setPos: (pos: [number, number, number]) => void;
  setRot: (rot: [number, number, number]) => void;
  onReset: () => void;
  onApply: () => void;
}) {
  const mkSetPos = (axis: 0 | 1 | 2) => (v: number) => {
    const newPos: [number, number, number] = [...pos];
    newPos[axis] = v;
    setPos(newPos);
  };
  const mkSetRot = (axis: 0 | 1 | 2) => (v: number) => {
    const newRot: [number, number, number] = [...rot];
    newRot[axis] = v;
    setRot(newRot);
  };

  const containerGap = useEditorStore(s => s.interfaceSettings.uiContainerGap);

  return (
    <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: containerGap }}>
      <SubLabel>{label} Position</SubLabel>
      <SliderRow label="Pos X" value={pos[0]} min={-10} max={10} step={0.1}
        format={v => v.toFixed(1)} onChange={mkSetPos(0)} />
      <SliderRow label="Pos Y" value={pos[1]} min={0} max={10} step={0.1}
        format={v => v.toFixed(1)} onChange={mkSetPos(1)} />
      <SliderRow label="Pos Z" value={pos[2]} min={-20} max={20} step={0.1}
        format={v => v.toFixed(1)} onChange={mkSetPos(2)} />

      <SubLabel>{label} Rotation</SubLabel>
      <SliderRow label="Rot X" value={rot[0]} min={-1.57} max={1.57} step={0.01}
        format={v => `${(v * 180 / Math.PI).toFixed(1)}\u00B0`} onChange={mkSetRot(0)} />
      <SliderRow label="Rot Y" value={rot[1]} min={-3.14} max={3.14} step={0.01}
        format={v => `${(v * 180 / Math.PI).toFixed(1)}\u00B0`} onChange={mkSetRot(1)} />
      <SliderRow label="Rot Z" value={rot[2]} min={-1.57} max={1.57} step={0.01}
        format={v => `${(v * 180 / Math.PI).toFixed(1)}\u00B0`} onChange={mkSetRot(2)} />

      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button style={applyBtnStyle} onClick={onApply}>
          Apply {label}
        </button>
      </div>
      <button style={{ ...resetBtnStyle, marginTop: 6 }} onClick={onReset}>
        Reset {label}
      </button>
    </div>
  );
}

function CameraSection() {
  const customPos = useEditorStore(s => s.customCameraPosition);
  const customRot = useEditorStore(s => s.customCameraRotation);
  const setCustomPos = useEditorStore(s => s.setCustomCameraPosition);
  const setCustomRot = useEditorStore(s => s.setCustomCameraRotation);
  const clearCustom = useEditorStore(s => s.clearCustomCamera);
  const defaultPos = useEditorStore(s => s.defaultCameraPosition);
  const defaultRot = useEditorStore(s => s.defaultCameraRotation);
  const setDefaultPos = useEditorStore(s => s.setDefaultCameraPosition);
  const setDefaultRot = useEditorStore(s => s.setDefaultCameraRotation);
  const leftPos = useEditorStore(s => s.leftCameraPosition);
  const leftRot = useEditorStore(s => s.leftCameraRotation);
  const setLeftPos = useEditorStore(s => s.setLeftCameraPosition);
  const setLeftRot = useEditorStore(s => s.setLeftCameraRotation);
  const setRightPos = useEditorStore(s => s.setRightCameraPosition);
  const setRightRot = useEditorStore(s => s.setRightCameraRotation);
  const resetPreset = useEditorStore(s => s.resetCameraPreset);
  const cameraView = useEditorStore(s => s.cameraView);
  const setCameraView = useEditorStore(s => s.setCameraView);

  const [openSection, setOpenSection] = React.useState<'default' | 'sides' | 'custom'>('custom');

  const builtinDef = BUILTIN_DEFAULTS['default'];
  const builtinLeft = BUILTIN_DEFAULTS['left'];
  const builtinCustom = BUILTIN_DEFAULTS['custom'];

  const applyView = (view: string) => {
    if (cameraView !== view) {
      setCameraView(view as any);
    } else {
      setCameraView('free');
      setTimeout(() => setCameraView(view as any), 30);
    }
  };

  const setSidesPos = (pos: [number, number, number]) => {
    setLeftPos(pos);
    setRightPos([-pos[0], pos[1], pos[2]]);
  };
  const setSidesRot = (rot: [number, number, number]) => {
    setLeftRot(rot);
    setRightRot([rot[0], -rot[1], -rot[2]]);
  };
  const resetSides = () => {
    resetPreset('left');
    resetPreset('right');
  };

  return (
    <SectionWrap>
      <HintText>
        Adjust position and rotation for each camera view. Changes persist across sessions.
      </HintText>

      {/* Default Camera */}
      <SectionBox>
        <button style={accordionBtnStyle} onClick={() => setOpenSection(openSection === 'default' ? 'custom' : 'default')}>
          <span>{openSection === 'default' ? '\u25BC' : '\u25B6'} Default Camera</span>
        </button>
        {openSection === 'default' && (
          <CameraPresetEditor
            label="Default" presetKey="default"
            pos={defaultPos || builtinDef.position}
            rot={defaultRot || builtinDef.rotation}
            setPos={setDefaultPos} setRot={setDefaultRot}
            onReset={() => resetPreset('default')}
            onApply={() => applyView('default')}
          />
        )}
      </SectionBox>

      {/* Sides Camera */}
      <SectionBox>
        <button style={accordionBtnStyle} onClick={() => setOpenSection(openSection === 'sides' ? 'custom' : 'sides')}>
          <span>{openSection === 'sides' ? '\u25BC' : '\u25B6'} Sides Camera</span>
        </button>
        {openSection === 'sides' && (
          <>
            <HintText style={{ marginTop: 4 }}>
              Edit the left side view — the right side is automatically mirrored.
            </HintText>
            <CameraPresetEditor
              label="Sides" presetKey="sides"
              pos={leftPos || builtinLeft.position}
              rot={leftRot || builtinLeft.rotation}
              setPos={setSidesPos} setRot={setSidesRot}
              onReset={resetSides}
              onApply={() => applyView('left')}
            />
          </>
        )}
      </SectionBox>

      {/* Custom Camera */}
      <SectionBox>
        <button style={accordionBtnStyle} onClick={() => setOpenSection(openSection === 'custom' ? 'default' : 'custom')}>
          <span>{openSection === 'custom' ? '\u25BC' : '\u25B6'} Custom Camera</span>
        </button>
        {openSection === 'custom' && (
          <>
            <CameraPresetEditor
              label="Custom" presetKey="custom"
              pos={customPos || builtinCustom.position}
              rot={customRot || builtinCustom.rotation}
              setPos={setCustomPos} setRot={setCustomRot}
              onReset={() => {
                setCustomPos([...builtinCustom.position]);
                setCustomRot([...builtinCustom.rotation]);
              }}
              onApply={() => applyView('custom')}
            />
            <button style={{ ...resetBtnStyle, marginTop: 8 }} onClick={clearCustom}>
              {'\u2715'} Clear Custom Camera
            </button>
          </>
        )}
      </SectionBox>
    </SectionWrap>
  );
}

// ── Color Picker Row ────────────────────────────────────────────────────────

function ColorRow({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const iface = useEditorStore(s => s.interfaceSettings);
  return (
    <div style={colorRowStyle}>
      <span style={{ ...colorLabelStyle, fontSize: iface.typoBodySize, color: color.ink }}>{label}</span>
      <input
        className="color-swatch"
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
      />
      <span style={{ ...colorHexStyle, fontSize: iface.typoValueSize, color: 'oklch(0.62 calc(0.05 * var(--accent-c)) var(--accent-h))' }}>{value.toUpperCase()}</span>
    </div>
  );
}

// ── Toggle Switch Row ────────────────────────────────────────────────────────

function ToggleRow({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  const iface = useEditorStore(s => s.interfaceSettings);
  return (
    <div style={toggleRowStyle}>
      <span style={{ ...colorLabelStyle, fontSize: iface.typoBodySize, color: color.ink }}>{label}</span>
      <button
        onClick={() => onChange(!value)}
        style={{
          ...toggleTrackStyle,
          background: value
            ? gradient.brand
            : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
          borderColor: value ? 'oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.4)' : 'rgba(80, 60, 140, 0.3)',
        }}
      >
        <div
          style={{
            ...toggleThumbStyle,
            transform: value ? 'translateX(14px)' : 'translateX(0)',
          }}
        />
      </button>
      <span style={{ fontSize: 9, color: color.ink, marginLeft: 4, fontFamily: font.sans }}>
        {value ? 'ON' : 'OFF'}
      </span>
    </div>
  );
}

// Segmented option picker (used by the Sound Effects console). Highlights the
// active value; wraps to a new line if the options don't fit.
function SegmentRow<T extends string>({ options, value, onChange }: {
  options: readonly T[]; value: T; onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
      {options.map(o => (
        <button
          key={o}
          onClick={() => onChange(o)}
          style={{
            ...segmentBtn(value === o),
            flex: '1 1 auto', justifyContent: 'center',
            textTransform: 'capitalize', fontSize: 10, fontWeight: 600, padding: '5px 8px',
          }}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

// ── Interface Section ─────────────────────────────────────────────────────────

function InterfaceSection() {
  const settings = useEditorStore(s => s.interfaceSettings);
  const setSettings = useEditorStore(s => s.setInterfaceSettings);
  const reset = useEditorStore(s => s.resetInterfaceSettings);

  const setNum = (field: keyof InterfaceSettings) => (v: number) => setSettings({ [field]: v } as any);
  const setColor = (field: keyof InterfaceSettings) => (v: string) => setSettings({ [field]: v } as any);
  const setBool = (field: keyof InterfaceSettings) => (v: boolean) => setSettings({ [field]: v } as any);

  return (
    <SectionWrap>
      <SectionBox title="Step Lines">
        <SliderRow label="Width" value={settings.stepLineWidth} min={0.2} max={4} step={0.1}
          format={v => v.toFixed(1)} onChange={setNum('stepLineWidth')} />
        <SliderRow label="Opacity" value={settings.stepLineOpacity} min={0.05} max={1} step={0.05}
          format={v => `${(v * 100).toFixed(0)}%`} onChange={setNum('stepLineOpacity')} />
        <ColorRow label="Color" value={settings.stepLineColor} onChange={setColor('stepLineColor')} />
      </SectionBox>

      <SectionBox title="Floor Grid">
        <SliderRow label="Beat Width" value={settings.gridLineWidth} min={0.1} max={3} step={0.1}
          format={v => v.toFixed(1)} onChange={setNum('gridLineWidth')} />
        <SliderRow label="Measure W" value={settings.gridMeasureLineWidth} min={0.5} max={5} step={0.25}
          format={v => v.toFixed(2)} onChange={setNum('gridMeasureLineWidth')} />
        <ToggleRow label="Beat Lines" value={settings.gridLineVisible} onChange={setBool('gridLineVisible')} />
        {/* Snap lines stay up during playback by default (reviewer feedback);
            this restores the old fade-out. */}
        <ToggleRow label="Fade Snap Lines on Play" value={settings.fadeBeatLinesOnPlay === true} onChange={setBool('fadeBeatLinesOnPlay')} />
        <ColorRow label="Beat Color" value={settings.gridLineColor} onChange={setColor('gridLineColor')} />
        <ColorRow label="Measure Color" value={settings.gridMeasureColor} onChange={setColor('gridMeasureColor')} />
        <ToggleRow label="Center Line" value={settings.gridCenterLineVisible} onChange={setBool('gridCenterLineVisible')} />
        <ColorRow label="Center Color" value={settings.gridCenterColor} onChange={setColor('gridCenterColor')} />
        <SliderRow label="Grid Y Offset" value={settings.gridYOffset} min={-2} max={2} step={0.05}
          format={v => v.toFixed(2)} onChange={setNum('gridYOffset')} />
      </SectionBox>

      <SectionBox title="Playhead / Trigger Area">
        <ToggleRow label="Floor Line" value={settings.playheadFloorVisible} onChange={setBool('playheadFloorVisible')} />
        <SliderRow label="Floor Width" value={settings.playheadLineWidth} min={0.5} max={8} step={0.5}
          format={v => v.toFixed(1)} onChange={setNum('playheadLineWidth')} />
        <ToggleRow label="Side Lines" value={settings.playheadSidesVisible} onChange={setBool('playheadSidesVisible')} />
        <SliderRow label="Side Width" value={settings.playheadSideLineWidth} min={0.5} max={5} step={0.25}
          format={v => v.toFixed(2)} onChange={setNum('playheadSideLineWidth')} />
        <ColorRow label="Color" value={settings.playheadColor} onChange={setColor('playheadColor')} />
      </SectionBox>

      <SectionBox title="Tunnel Edges">
        <SliderRow label="Width" value={settings.edgeLineWidth} min={0.5} max={5} step={0.25}
          format={v => v.toFixed(2)} onChange={setNum('edgeLineWidth')} />
        <ColorRow label="Primary" value={settings.edgePrimaryColor} onChange={setColor('edgePrimaryColor')} />
        <ColorRow label="Secondary" value={settings.edgeSecondaryColor} onChange={setColor('edgeSecondaryColor')} />
      </SectionBox>

      <SectionBox title="Background">
        <ColorRow label="Top Color" value={settings.bgGradientTop} onChange={setColor('bgGradientTop')} />
        <ColorRow label="Bottom Color" value={settings.bgGradientBottom} onChange={setColor('bgGradientBottom')} />
      </SectionBox>

      <SectionBox title="Beat Number Labels">
        <SliderRow label="Size" value={settings.beatNumberSize} min={0.1} max={0.5} step={0.02}
          format={v => v.toFixed(2)} onChange={setNum('beatNumberSize')} />
      </SectionBox>

      <SectionBox title="Floor Plane">
        <SliderRow label="Opacity" value={settings.floorOpacity} min={0} max={1} step={0.05}
          format={v => `${(v * 100).toFixed(0)}%`} onChange={setNum('floorOpacity')} />
        <ColorRow label="Color" value={settings.floorColor} onChange={setColor('floorColor')} />
      </SectionBox>

      <SectionBox title="3D Lighting">
        <ColorRow label="Light Color" value={settings.lightColor} onChange={setColor('lightColor')} />
        <SliderRow label="Intensity" value={settings.lightIntensity} min={0} max={2} step={0.05}
          format={v => v.toFixed(2)} onChange={setNum('lightIntensity')} />
        <SliderRow label="Rotation X" value={settings.lightRotationX} min={-180} max={180} step={5}
          format={v => `${v.toFixed(0)}\u00B0`} onChange={setNum('lightRotationX')} />
        <SliderRow label="Rotation Y" value={settings.lightRotationY} min={-180} max={180} step={5}
          format={v => `${v.toFixed(0)}\u00B0`} onChange={setNum('lightRotationY')} />
        <ColorRow label="Ambient Color" value={settings.ambientColor} onChange={setColor('ambientColor')} />
        <SliderRow label="Ambient Int." value={settings.ambientIntensity} min={0} max={1} step={0.05}
          format={v => v.toFixed(2)} onChange={setNum('ambientIntensity')} />
      </SectionBox>

      <SectionBox title="Scene Atmosphere">
        <SliderRow label="Fog Near" value={settings.fogNear} min={0} max={50} step={1}
          format={v => v.toFixed(0)} onChange={setNum('fogNear')} />
        <SliderRow label="Fog Far" value={settings.fogFar} min={20} max={500} step={5}
          format={v => v.toFixed(0)} onChange={setNum('fogFar')} />
        <SliderRow label="Camera FOV" value={settings.cameraFov} min={30} max={120} step={1}
          format={v => `${v.toFixed(0)}\u00B0`} onChange={setNum('cameraFov')} />
        {/* How fast the view travels to a new spot when you jump between notes
            or scrub while paused. 0 = teleport (the old behaviour). */}
        <SliderRow label="Camera Travel Speed" value={settings.cameraTravelSpeed ?? 6} min={0} max={10} step={1}
          format={v => (v === 0 ? 'Instant' : `${v.toFixed(0)}`)} onChange={setNum('cameraTravelSpeed')} />
      </SectionBox>

      <SectionBox title="Point Lights">
        <ColorRow label="Light 1 Color" value={settings.pointLight1Color} onChange={setColor('pointLight1Color')} />
        <SliderRow label="Light 1 Power" value={settings.pointLight1Intensity} min={0} max={2} step={0.05}
          format={v => v.toFixed(2)} onChange={setNum('pointLight1Intensity')} />
        <ColorRow label="Light 2 Color" value={settings.pointLight2Color} onChange={setColor('pointLight2Color')} />
        <SliderRow label="Light 2 Power" value={settings.pointLight2Intensity} min={0} max={2} step={0.05}
          format={v => v.toFixed(2)} onChange={setNum('pointLight2Intensity')} />
        <ColorRow label="Light 3 Color" value={settings.pointLight3Color} onChange={setColor('pointLight3Color')} />
        <SliderRow label="Light 3 Power" value={settings.pointLight3Intensity} min={0} max={2} step={0.05}
          format={v => v.toFixed(2)} onChange={setNum('pointLight3Intensity')} />
      </SectionBox>

      <SectionBox title="Note Appearance">
        <SliderRow label="Emissive (Normal)" value={settings.noteEmissiveNormal} min={0} max={2} step={0.05}
          format={v => v.toFixed(2)} onChange={setNum('noteEmissiveNormal')} />
        <SliderRow label="Emissive (Stem)" value={settings.noteEmissiveStemAura} min={0} max={2} step={0.05}
          format={v => v.toFixed(2)} onChange={setNum('noteEmissiveStemAura')} />
        <SliderRow label="Emissive (Past)" value={settings.noteEmissivePast} min={0} max={2} step={0.05}
          format={v => v.toFixed(2)} onChange={setNum('noteEmissivePast')} />
        <SliderRow label="Opacity (Past)" value={settings.noteOpacityPast} min={0} max={1} step={0.05}
          format={v => v.toFixed(2)} onChange={setNum('noteOpacityPast')} />
        <SliderRow label="Rail Opacity" value={settings.railOpacity} min={0} max={1} step={0.05}
          format={v => v.toFixed(2)} onChange={setNum('railOpacity')} />
      </SectionBox>

      <SectionBox title="Notes Grid">
        <GridSettings />
      </SectionBox>

      <button style={resetBtnStyle} onClick={reset}>
        Reset Interface Defaults
      </button>
    </SectionWrap>
  );
}


// ── Grid Settings Sub-section ────────────────────────────────────────────────

function GridSettings() {
  const gridVisible = useEditorStore(s => s.gridVisible);
  const snapToGrid = useEditorStore(s => s.snapToGrid);
  const gridGuideVisible = useEditorStore(s => s.gridGuideVisible);
  const gridWidth = useEditorStore(s => s.gridWidth);
  const gridHeight = useEditorStore(s => s.gridHeight);
  const gridDivisionsH = useEditorStore(s => s.gridDivisionsH);
  const gridDivisionsV = useEditorStore(s => s.gridDivisionsV);
  const gridColor = useEditorStore(s => s.gridColor);
  const gridOpacity = useEditorStore(s => s.gridOpacity);
  const notesGridYOffset = useEditorStore(s => s.notesGridYOffset);
  const gs = () => useEditorStore.getState();

  return (
    <>
      <ToggleRow label="Grid Visible" value={gridVisible}
        onChange={v => gs().setGridVisible(v)} />
      <ToggleRow label="Show Grid Lines" value={gridGuideVisible}
        onChange={v => gs().setGridGuideVisible(v)} />
      <ToggleRow label="Snap to Grid" value={snapToGrid}
        onChange={v => gs().setSnapToGrid(v)} />

      {/* Beat snapping is governed by the transport's "Step" control (which also
          covers triplets), so a second, conflicting division picker lived here
          doing nothing to placement. Removed rather than left as a dead control. */}

      <SubLabel>Grid Size</SubLabel>
      <SliderRow label="Width" value={gridWidth} min={1} max={8} step={0.25}
        format={v => v.toFixed(2)} onChange={v => gs().setGridWidth(v)} />
      <SliderRow label="Height" value={gridHeight} min={1} max={6} step={0.25}
        format={v => v.toFixed(2)} onChange={v => gs().setGridHeight(v)} />
      <SliderRow label="Y Offset" value={notesGridYOffset} min={-2} max={2} step={0.05}
        format={v => v.toFixed(2)} onChange={v => gs().setNotesGridYOffset(v)} />

      <SubLabel>Divisions</SubLabel>
      <SliderRow label="Horizontal" value={gridDivisionsH} min={2} max={20} step={1}
        format={v => v.toFixed(0)} onChange={v => gs().setGridDivisionsH(Math.round(v))} />
      <SliderRow label="Vertical" value={gridDivisionsV} min={2} max={16} step={1}
        format={v => v.toFixed(0)} onChange={v => gs().setGridDivisionsV(Math.round(v))} />

      <SubLabel>Appearance</SubLabel>
      <ColorRow label="Color" value={gridColor} onChange={v => gs().setGridColor(v)} />
      <SliderRow label="Opacity" value={gridOpacity} min={0.05} max={0.8} step={0.05}
        format={v => (v * 100).toFixed(0) + '%'} onChange={v => gs().setGridOpacity(v)} />
    </>
  );
}

// ── Particles Section ────────────────────────────────────────────────────────

function ParticlesSection() {
  const settings = useEditorStore(s => s.particleSettings);
  const setSettings = useEditorStore(s => s.setParticleSettings);
  const reset = useEditorStore(s => s.resetParticleSettings);

  const set = (field: keyof ParticleSettings) => (v: number) => setSettings({ [field]: v });

  return (
    <SectionWrap>
      <SectionBox title="Spark Particles">
        <ToggleRow label="Loop" value={settings.sparkLoop}
          onChange={v => setSettings({ sparkLoop: v })} />
        <SliderRow label="Count" value={settings.sparkCount} min={50} max={5000} step={10}
          format={v => `${v.toFixed(0)}`} onChange={set('sparkCount')} />
        <SliderRow label="Size" value={settings.sparkSize} min={1} max={20} step={0.5}
          format={v => `${v.toFixed(1)}px`} onChange={set('sparkSize')} />
      </SectionBox>

      <SectionBox title="Colors">
        <ColorRow label="Color 1" value={settings.sparkColor}
          onChange={v => setSettings({ sparkColor: v })} />
        <ColorRow label="Color 2" value={settings.sparkColor2}
          onChange={v => setSettings({ sparkColor2: v })} />
        <ColorRow label="Color 3" value={settings.sparkColor3}
          onChange={v => setSettings({ sparkColor3: v })} />
        <ColorRow label="Color 4" value={settings.sparkColor4}
          onChange={v => setSettings({ sparkColor4: v })} />
        <ToggleRow label="Blend Colors" value={settings.sparkColorBlend}
          onChange={v => setSettings({ sparkColorBlend: v })} />
        {settings.sparkColorBlend && (
          <SliderRow label="Transition" value={settings.sparkColorTransition} min={0.5} max={10} step={0.25}
            format={v => `${v.toFixed(2)}s`} onChange={set('sparkColorTransition')} />
        )}
      </SectionBox>

      <SectionBox title="Motion">
        <SliderRow label="Speed Min" value={settings.sparkSpeedMin} min={0.5} max={10} step={0.25}
          format={v => v.toFixed(2)} onChange={set('sparkSpeedMin')} />
        <SliderRow label="Speed Max" value={settings.sparkSpeedMax} min={1} max={20} step={0.25}
          format={v => v.toFixed(2)} onChange={set('sparkSpeedMax')} />
        <SliderRow label="Turbulence" value={settings.sparkTurbulence} min={0} max={5} step={0.1}
          format={v => v.toFixed(1)} onChange={set('sparkTurbulence')} />
      </SectionBox>

      <SectionBox title="Spread">
        <SliderRow label="Horizontal" value={settings.sparkSpreadX} min={1} max={30} step={0.5}
          format={v => v.toFixed(1)} onChange={set('sparkSpreadX')} />
        <SliderRow label="Depth" value={settings.sparkSpreadZ} min={1} max={500} step={1}
          format={v => v.toFixed(1)} onChange={set('sparkSpreadZ')} />
        <SliderRow label="Y Min" value={settings.sparkYMin} min={-10} max={0} step={0.5}
          format={v => v.toFixed(1)} onChange={set('sparkYMin')} />
        <SliderRow label="Y Max" value={settings.sparkYMax} min={3} max={30} step={0.5}
          format={v => v.toFixed(1)} onChange={set('sparkYMax')} />
      </SectionBox>

      <SectionBox title="Position">
        <SliderRow label="Offset X" value={settings.sparkOffsetX} min={-15} max={15} step={0.5}
          format={v => v.toFixed(1)} onChange={set('sparkOffsetX')} />
        <SliderRow label="Offset Y" value={settings.sparkOffsetY} min={-10} max={10} step={0.5}
          format={v => v.toFixed(1)} onChange={set('sparkOffsetY')} />
        <SliderRow label="Offset Z" value={settings.sparkOffsetZ} min={-20} max={10} step={0.5}
          format={v => v.toFixed(1)} onChange={set('sparkOffsetZ')} />
      </SectionBox>

      <SectionBox title="Direction">
        <SliderRow label="Angle" value={settings.sparkDirectionAngle} min={0} max={360} step={5}
          format={v => {
            const labels: Record<number, string> = { 0: 'Up', 90: 'Right', 180: 'Down', 270: 'Left' };
            return labels[v] || `${v.toFixed(0)}\u00B0`;
          }} onChange={set('sparkDirectionAngle')} />
        <SliderRow label="Tilt" value={settings.sparkDirectionTilt} min={-90} max={90} step={5}
          format={v => v === 0 ? 'None' : `${v.toFixed(0)}\u00B0`} onChange={set('sparkDirectionTilt')} />
      </SectionBox>

      <SectionBox title="Rotation">
        <SliderRow label="Rotation X" value={settings.sparkRotationX} min={-180} max={180} step={5}
          format={v => v === 0 ? 'None' : `${v.toFixed(0)}\u00B0/s`} onChange={set('sparkRotationX')} />
        <SliderRow label="Rotation Y" value={settings.sparkRotationY} min={-180} max={180} step={5}
          format={v => v === 0 ? 'None' : `${v.toFixed(0)}\u00B0/s`} onChange={set('sparkRotationY')} />
      </SectionBox>

      <SectionBox title="Fade">
        <SliderRow label="Fade In" value={settings.sparkFadeInSpeed} min={0.5} max={15} step={0.25}
          format={v => v.toFixed(2)} onChange={set('sparkFadeInSpeed')} />
        <SliderRow label="Fade Out" value={settings.sparkFadeOutSpeed} min={0.25} max={10} step={0.25}
          format={v => v.toFixed(2)} onChange={set('sparkFadeOutSpeed')} />
      </SectionBox>

      <button
        style={{
          background: 'rgba(232, 180, 248, 0.12)',
          border: '1px solid rgba(232, 180, 248, 0.35)',
          color: color.ink,
          borderRadius: 8,
          padding: '7px 12px',
          cursor: 'pointer',
          fontSize: 10,
          fontWeight: 600,
          fontFamily: font.sans,
          width: '100%',
          marginTop: 6,
          transition: 'all 0.2s ease',
        }}
        onClick={() => {
          setSettings({ sparkLoop: true });
          setTimeout(() => setSettings({ sparkLoop: false }), 4000);
        }}
      >
        Preview Particles
      </button>

      <button style={resetBtnStyle} onClick={reset}>
        Reset Particle Defaults
      </button>
    </SectionWrap>
  );
}

// ── Keybindings Section ──────────────────────────────────────────────────────

function KeybindingsSection() {
  const bindings = useEditorStore(s => s.keybindings);
  const resetKeybindings = useEditorStore(s => s.resetKeybindings);
  const setKeybinding = useEditorStore(s => s.setKeybinding);
  const [listening, setListening] = React.useState<{ id: string; slot: 'primary' | 'secondary' } | null>(null);

  React.useEffect(() => {
    if (!listening) return;
    const handler = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape') {
        setListening(null);
        return;
      }
      const mods: Array<'ctrl' | 'alt' | 'shift'> = [];
      if (e.ctrlKey || e.metaKey) mods.push('ctrl');
      if (e.altKey) mods.push('alt');
      if (e.shiftKey) mods.push('shift');
      if (['Control', 'Alt', 'Shift', 'Meta'].includes(e.key)) return;

      let key = e.key.toLowerCase();
      if (e.code.startsWith('Numpad')) key = e.code;
      const specials: Record<string, string> = {
        'arrowup': 'ArrowUp', 'arrowdown': 'ArrowDown',
        'arrowleft': 'ArrowLeft', 'arrowright': 'ArrowRight',
        'delete': 'Delete', 'backspace': 'Backspace',
        'home': 'Home', 'end': 'End', 'pageup': 'PageUp',
        'pagedown': 'PageDown', 'tab': 'Tab', ' ': 'Space',
        'enter': 'Enter',
      };
      if (specials[key]) key = specials[key];
      if (e.key.startsWith('F') && e.key.length > 1) key = e.key;

      setKeybinding(listening.id, listening.slot, { key, mods });
      setListening(null);
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [listening, setKeybinding]);

  const categories = React.useMemo(() => {
    const map = new Map<string, typeof bindings>();
    for (const b of bindings) {
      const list = map.get(b.category) || [];
      list.push(b);
      map.set(b.category, list);
    }
    return map;
  }, [bindings]);

  const categoryOrder = ['input_modifiers', 'map_editing', 'timeline', 'interface', 'camera', 'volume'];
  const categoryLabels: Record<string, string> = {
    input_modifiers: 'Input Modifiers',
    map_editing: 'Map Editing Controls',
    timeline: 'Timeline Controls',
    interface: 'Interface Controls',
    camera: 'Camera Controls',
    volume: 'Volume Controls',
  };

  const formatCombo = (combo: { key: string; mods: Array<'ctrl' | 'alt' | 'shift'> }) => {
    if (combo.key === 'None') return 'None';
    const parts: string[] = [];
    if (combo.mods.includes('ctrl')) parts.push('Ctrl');
    if (combo.mods.includes('alt')) parts.push('Alt');
    if (combo.mods.includes('shift')) parts.push('Shift');
    let keyName = combo.key;
    if (keyName.length === 1) keyName = keyName.toUpperCase();
    parts.push(keyName);
    return parts.join('+');
  };

  return (
    <SectionWrap>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ color: 'oklch(0.82 calc(0.12 * var(--accent-c)) var(--accent-h))', fontSize: 13, fontWeight: 700, fontFamily: font.sans }}>Keybindings</span>
        <button style={{ ...resetBtnStyle, marginTop: 0, width: 'auto', padding: '5px 12px' }} onClick={resetKeybindings}>
          Reset All
        </button>
      </div>

      {categoryOrder.map(cat => {
        const items = categories.get(cat);
        if (!items || items.length === 0) return null;
        return (
          <SectionBox key={cat} title={categoryLabels[cat]}>
            {/* Header row */}
            <div style={kbHeaderRow}>
              <span style={{ flex: 2, color: color.ink, fontSize: 9, fontFamily: font.sans }}>Action</span>
              <span style={{ flex: 2, color: color.ink, fontSize: 9, textAlign: 'center', fontFamily: font.sans }}>Bindings</span>
            </div>
            {items.map(b => {
              const isPrimListening = listening?.id === b.id && listening.slot === 'primary';
              const isSecListening = listening?.id === b.id && listening.slot === 'secondary';
              return (
                <div key={b.id} style={kbRow}>
                  <span style={{ flex: 2, color: color.ink, fontSize: 10, fontFamily: font.sans }}>{b.description}</span>
                  <div style={{ flex: 2, display: 'flex', gap: 4, justifyContent: 'center' }}>
                    <button
                      style={{
                        ...kbKeyBtn,
                        borderColor: isPrimListening ? color.accent : 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)',
                        background: isPrimListening ? 'oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.15)' : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.6)',
                      }}
                      onClick={() => setListening(isPrimListening ? null : { id: b.id, slot: 'primary' })}
                    >
                      {isPrimListening ? '...' : formatCombo(b.primary)}
                    </button>
                    <button
                      style={{
                        ...kbKeyBtn,
                        borderColor: isSecListening ? color.accent : 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)',
                        background: isSecListening ? 'oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.15)' : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.6)',
                      }}
                      onClick={() => setListening(isSecListening ? null : { id: b.id, slot: 'secondary' })}
                    >
                      {isSecListening ? '...' : formatCombo(b.secondary)}
                    </button>
                  </div>
                </div>
              );
            })}
          </SectionBox>
        );
      })}
    </SectionWrap>
  );
}

// ── UI Section ──────────────────────────────────────────────────────────────

function UISection() {
  const settings = useEditorStore(s => s.interfaceSettings);
  const setSettings = useEditorStore(s => s.setInterfaceSettings);
  const renderMode = useEditorStore(s => s.renderMode);
  const setRenderMode = useEditorStore(s => s.setRenderMode);
  const tooltipsEnabled = useTheme(s => s.tooltipsEnabled);
  const setTooltipsEnabled = useTheme(s => s.setTooltipsEnabled);

  const setNum = (field: keyof InterfaceSettings) => (v: number) => setSettings({ [field]: v } as any);
  const setColor = (field: keyof InterfaceSettings) => (v: string) => setSettings({ [field]: v } as any);

  return (
    <SectionWrap>
      <SectionBox title="Theme">
        <ThemeControls />
      </SectionBox>

      <SectionBox title="Interface">
        <HintText>
          Show the rich hover tooltips across the editor.
        </HintText>
        <ToggleRow label="Tooltips" value={tooltipsEnabled} onChange={setTooltipsEnabled} />
      </SectionBox>

      <SectionBox title="Rendering">
        <HintText>
          Force GPU or CPU rendering, or let the browser decide. Changing reloads the editor.
        </HintText>
        <div style={{ display: 'flex', gap: 6 }}>
          {(['auto', 'gpu', 'cpu'] as const).map(m => (
            <button
              key={m}
              onClick={() => { if (m !== renderMode) { setRenderMode(m); window.location.reload(); } }}
              style={{
                ...segmentBtn(renderMode === m),
                flex: 1, justifyContent: 'center',
                textTransform: 'uppercase', fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
              }}
            >
              {m}
            </button>
          ))}
        </div>
      </SectionBox>

    </SectionWrap>
  );
}

